import React, { useState, useEffect, useCallback } from 'react';
import {
  X, RotateCcw, ArrowLeftRight, Percent, Pause, Play, Gift, User,
  Receipt, Coins, Tag, AlertTriangle, Unlock, Calculator, BarChart3,
  Clock, Info, Power, AlertCircle, Save, Search, Lock, Package,
  Scale, SearchX, ArrowUpFromLine, PackagePlus, ClipboardList,
  Sparkles, Inbox, Monitor, Ticket, Fuel, CalendarClock,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import SetupPosCard, { type LastSetup } from './SetupPosCard';
import { resolveAllowedButtonIds } from './posButtonEntitlements';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  terminal: { terminalId: string; locationId: string; registerName: string };
  tenantId: string;
  /** Told after a successful save, so a host that shows this inside a larger
   *  flow (the order's setup rail) can refresh its own view of the register. */
  onSaved?: () => void;
}

// ---------------------------------------------------------------------------
// Button Registry — mirrors POS_BUTTON_REGISTRY from shared-types
// When adding a new button, add here + shared-types + POS handlers
// ---------------------------------------------------------------------------

const ICON_MAP: Record<string, React.FC<any>> = {
  X, RotateCcw, ArrowLeftRight, Percent, Pause, Play, Gift, User,
  Receipt, Coins, Tag, AlertTriangle, Unlock, Calculator, BarChart3,
  Clock, Info, Power, Search, Lock, Package, Scale, SearchX, ArrowUpFromLine, PackagePlus, ClipboardList,
  Sparkles, Inbox, Monitor, Ticket, Fuel, CalendarClock,
};

interface ButtonDef {
  id: string;
  featureGate: string;
  group: 'sales' | 'inventory' | 'register' | 'employee' | 'admin';
  label: string;
  description: string;
  color: string;
  icon: string;
}

// Must match POS_BUTTON_REGISTRY in @platform/shared-types/packages.ts
export const ALL_BUTTONS: ButtonDef[] = [
  { id: 'void', featureGate: 'pos.button.void', group: 'sales', label: 'Void', description: 'Void current transaction', color: '#ef4444', icon: 'X' },
  { id: 'refund', featureGate: 'pos.button.refund', group: 'sales', label: 'Refund', description: 'Process refunds', color: '#f87171', icon: 'RotateCcw' },
  { id: 'exchange', featureGate: 'pos.button.exchange', group: 'sales', label: 'Exchange', description: 'Exchange items', color: '#fb923c', icon: 'ArrowLeftRight' },
  { id: 'discount', featureGate: 'pos.button.discount', group: 'sales', label: 'Discount', description: 'Apply order discounts', color: '#60a5fa', icon: 'Percent' },
  { id: 'hold', featureGate: 'pos.button.hold', group: 'sales', label: 'Hold', description: 'Hold current order', color: '#a78bfa', icon: 'Pause' },
  { id: 'open-order', featureGate: 'pos.button.open-order', group: 'sales', label: 'Open Order', description: 'Recall held orders', color: '#c084fc', icon: 'Play' },
  { id: 'search', featureGate: 'pos.button.search', group: 'sales', label: 'Search', description: 'Product search popup', color: '#3b82f6', icon: 'Search' },
  { id: 'customer', featureGate: 'pos.button.customer', group: 'sales', label: 'Customer', description: 'Customer lookup', color: '#3b82f6', icon: 'User' },
  { id: 'rewards', featureGate: 'pos.button.rewards', group: 'sales', label: 'Rewards', description: 'Loyalty rewards', color: '#4ade80', icon: 'Gift' },
  { id: 'transactions', featureGate: 'pos.button.transactions', group: 'sales', label: 'Transactions', description: 'Transaction history', color: '#a5b4fc', icon: 'Receipt' },
  { id: 'cash-tip', featureGate: 'pos.button.cash-tip', group: 'sales', label: 'Cash Tip', description: 'Cash tip entry', color: '#4ade80', icon: 'Coins' },
  { id: 'deals', featureGate: 'pos.button.deals', group: 'sales', label: 'Deals', description: 'Deals & promotions', color: '#fbbf24', icon: 'Tag' },
  { id: 'open-drawer', featureGate: 'pos.button.open-drawer', group: 'register', label: 'Open Drawer', description: 'Open cash drawer', color: '#60a5fa', icon: 'Unlock' },
  { id: 'safe-drop', featureGate: 'pos.button.safe-drop', group: 'register', label: 'Safe Drop', description: 'Move excess cash from drawer to safe', color: '#ef4444', icon: 'Lock' },
  { id: 'payout', featureGate: 'pos.button.payout', group: 'register', label: 'Payout', description: 'Record business expense cash withdrawal', color: '#f87171', icon: 'ArrowUpFromLine' },
  { id: 'new-product-request', featureGate: 'pos.button.new-product-request', group: 'register', label: 'New Product', description: 'Record customer request for a new product', color: '#f59e0b', icon: 'PackagePlus' },
  { id: 'calculator', featureGate: 'pos.button.calculator', group: 'register', label: 'Calculator', description: 'On-screen calculator', color: '#60a5fa', icon: 'Calculator' },
  { id: 'reports', featureGate: 'pos.button.reports', group: 'register', label: 'Reports', description: 'Shift reports', color: '#60a5fa', icon: 'BarChart3' },
  { id: 'clock', featureGate: 'pos.button.clock', group: 'employee', label: 'Clock In/Out', description: 'Employee time clock', color: '#a78bfa', icon: 'Clock' },
  { id: 'shift-info', featureGate: 'pos.button.shift-info', group: 'employee', label: 'Shift Info', description: 'Current shift details', color: '#a78bfa', icon: 'Info' },
  { id: 'end-shift', featureGate: 'pos.button.end-shift', group: 'admin', label: 'End Shift', description: 'Close register / end shift', color: '#ef4444', icon: 'Power' },
  { id: 'store-supply', featureGate: 'pos.button.store-supply', group: 'sales', label: 'Store Supply', description: 'Check and report store supply inventory levels', color: '#8b5cf6', icon: 'Package' },
  { id: 'weighted-lookup', featureGate: 'pos.button.weighted-lookup', group: 'sales', label: 'Weighted', description: 'Browse and add weighted products', color: '#f59e0b', icon: 'Scale' },
  { id: 'non-scannable-lookup', featureGate: 'pos.button.non-scannable-lookup', group: 'sales', label: 'Non-Scannable', description: 'Browse and add non-scannable products', color: '#6366f1', icon: 'SearchX' },
  { id: 'pick-list', featureGate: 'pos.button.pick-list', group: 'inventory', label: 'Pick List', description: 'View and process pick lists', color: '#14b8a6', icon: 'ClipboardList' },
  { id: 'transfer', featureGate: 'pos.button.transfer', group: 'inventory', label: 'Transfers', description: 'View and process stock transfers', color: '#0ea5e9', icon: 'ArrowLeftRight' },
  { id: 'damage', featureGate: 'pos.button.damage', group: 'inventory', label: 'Damage', description: 'Mark items as damaged', color: '#f97316', icon: 'AlertTriangle' },
  // Module 29 — Oil Blending. Gated by pkg_pos_oil_blending; the API filters
  // by enabled features so these only appear in "Available" for tenants with
  // the Ruby Oil Blender package on.
  { id: 'build-blend', featureGate: 'pos.button.build-blend', group: 'sales', label: 'Build Blend', description: 'Create a custom oil blend at the counter', color: '#10b981', icon: 'Sparkles' },
  { id: 'pending-blends', featureGate: 'pos.button.pending-blends', group: 'sales', label: 'Pending Blends', description: 'Customer-submitted blends waiting at the register', color: '#10b981', icon: 'Inbox' },
  { id: 'customer-display', featureGate: 'pos.button.customer-display', group: 'register', label: 'Customer Display', description: 'Open the customer-facing portal on a secondary display', color: '#3b82f6', icon: 'Monitor' },
  { id: 'lottery', featureGate: 'pos.button.lottery', group: 'sales', label: 'Lotto', description: 'Lottery & gaming: sell, pay out, count', color: '#7c3aed', icon: 'Ticket' },
  // Gas & Fuel — in Ruby Core, so every tenant can add it; tills show it only
  // where the location has gas switched on in the back office.
  { id: 'gas', featureGate: 'pos.button.gas', group: 'sales', label: 'Gas', description: 'Sell prepaid fuel by amount or gallons', color: '#d97706', icon: 'Fuel' },
  // Services (pkg_services) — appointments, walk-ins, job tickets, rentals.
  { id: 'services', featureGate: 'pos.button.services', group: 'sales', label: 'Services', description: 'Appointments, walk-ins, job tickets and rentals', color: '#0d9488', icon: 'CalendarClock' },
];

const GROUP_ORDER = ['sales', 'inventory', 'register', 'employee', 'admin'];
const GROUP_LABELS: Record<string, string> = { sales: 'Sales', inventory: 'Inventory Operations', register: 'Register', employee: 'Employee', admin: 'Admin' };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PosButtonLayoutTab: React.FC<Props> = ({ terminal, tenantId, onSaved }) => {
  const [available, setAvailable] = useState<string[]>([]);
  const [pinned, setPinned] = useState<string[]>([]);
  const [initialAvailable, setInitialAvailable] = useState<string[]>([]);
  const [allowedButtonIds, setAllowedButtonIds] = useState<string[] | null>(null); // null = loading
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Read-only mirrors of the two subfields this tab does NOT edit. We round-
  // trip them on save so the tab never drops per-terminal customizations that
  // were set in the platform-app Keypad Designer.
  const [keypadSlots, setKeypadSlots] = useState<Record<string, any>>({});
  const [buttonCustomizations, setButtonCustomizations] = useState<Record<string, any>>({});
  const [lastSetup, setLastSetup] = useState<LastSetup | null>(null);

  const registerId = terminal.terminalId;

  /* The tenant's entitled buttons — the resolver lives in
     posButtonEntitlements.ts so the order screen's setup runner enables the
     SAME set this tab shows, rather than a second copy that drifts. */
  const fetchAllowedButtons = useCallback(async () => {
    setAllowedButtonIds(await resolveAllowedButtonIds(tenantId, ALL_BUTTONS));
  }, [tenantId]);

  // Fetch register data
  //
  // IMPORTANT: we also read `keypadSlots` and `buttonCustomizations` here —
  // not to let the super admin edit them (those live in the platform-app's
  // Keypad Designer), but so we can ROUND-TRIP them intact on save.
  //
  // Previously this tab only read `available` + `pinned`, and the save
  // below sent only those two fields. The backend's update handler does
  // `SET buttonLayout = :buttonLayout` (full replace), so saving here
  // silently wiped `keypadSlots` + `buttonCustomizations` from DynamoDB —
  // destroying per-terminal customizations built elsewhere.
  const fetchRegister = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await api.posConfig.listRegisters(tenantId);
      const registers = resp?.registers || [];
      const register = registers.find((r: any) =>
        r.registerId === registerId || r.terminalId === registerId
      );
      setLastSetup(typeof register?.posSetupAt === 'number'
        ? {
            at: register.posSetupAt,
            // Registers set up before the count existed have run it at least once.
            count: typeof register.posSetupCount === 'number' ? register.posSetupCount : 1,
            steps: Array.isArray(register.posSetupSteps) ? register.posSetupSteps : [],
          }
        : null);
      const bl = register?.buttonLayout || {};
      const a = Array.isArray(bl.available) ? bl.available : [];
      setPinned(Array.isArray(bl.pinned) ? bl.pinned : []);
      setAvailable(a);
      setInitialAvailable(a);
      setKeypadSlots(bl.keypadSlots && typeof bl.keypadSlots === 'object' ? bl.keypadSlots : {});
      setButtonCustomizations(
        bl.buttonCustomizations && typeof bl.buttonCustomizations === 'object'
          ? bl.buttonCustomizations
          : {},
      );
    } catch (err: any) {
      setError(err.message || 'Failed to load register data');
    } finally {
      setLoading(false);
    }
  }, [registerId, tenantId]);

  useEffect(() => {
    fetchAllowedButtons();
    fetchRegister();
  }, [fetchAllowedButtons, fetchRegister]);

  // Filter ALL_BUTTONS to only those allowed by package
  const visibleButtons = allowedButtonIds
    ? ALL_BUTTONS.filter((btn) => allowedButtonIds.includes(btn.id))
    : [];

  // Group visible buttons
  const groupedButtons = GROUP_ORDER
    .map((group) => ({
      group,
      label: GROUP_LABELS[group],
      buttons: visibleButtons.filter((btn) => btn.group === group),
    }))
    .filter((g) => g.buttons.length > 0);

  const allVisibleIds = visibleButtons.map((b) => b.id);
  const allEnabled = allVisibleIds.length > 0 && allVisibleIds.every((id) => available.includes(id));

  const isDirty = JSON.stringify([...available].sort()) !== JSON.stringify([...initialAvailable].sort());

  const handleToggle = (buttonId: string) => {
    setAvailable((prev) => {
      if (prev.includes(buttonId)) {
        return prev.filter((id) => id !== buttonId);
      }
      return [...prev, buttonId];
    });
  };

  const handleEnableAll = () => {
    if (allEnabled) {
      setAvailable([]);
    } else {
      setAvailable([...allVisibleIds]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Keep pinned as subset of available
      const validPinned = pinned.filter((id) => available.includes(id));
      // CRITICAL: round-trip keypadSlots + buttonCustomizations. This tab
      // doesn't edit them, but the backend update handler replaces the
      // whole `buttonLayout` object with whatever we send — so if we omit
      // them, they get wiped from DynamoDB (this exact bug destroyed
      // per-terminal customizations built in the platform-app Keypad
      // Designer). Send them back exactly as loaded.
      await api.posConfig.updateRegister(registerId, {
        tenantId,
        buttonLayout: {
          available,
          pinned: validPinned,
          keypadSlots,
          buttonCustomizations,
        },
      });
      setInitialAvailable([...available]);
      setPinned(validPinned);
      toastService.success(`Button layout updated — ${available.length} button${available.length === 1 ? '' : 's'} on ${terminal.registerName}.`);
      onSaved?.();
    } catch (err: any) {
      toastService.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || allowedButtonIds === null) {
    return (
      <RubyLoader variant="page" className="py-16" />
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-8 text-center">
        <AlertCircle size={28} className="mx-auto mb-3 text-red-400" />
        <p className="text-stone-600 text-sm">{error}</p>
        <button onClick={fetchRegister} className="mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium">Retry</button>
      </div>
    );
  }

  if (visibleButtons.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-8 text-center">
        <Lock size={28} className="mx-auto mb-3 text-stone-300" />
        <p className="text-stone-600 text-sm font-medium">No POS buttons available</p>
        <p className="text-stone-400 text-xs mt-1">This tenant's packages don't include any POS terminal button features. Assign POS packages first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        {/* The button must not be what gives way. This tab also renders inside
            the order's setup rail, which is far narrower than the terminal
            page — there the description squeezed the action until "Save
            Changes" wrapped onto two lines and the control ballooned. The TEXT
            shrinks (min-w-0), the button does not (shrink-0, nowrap). */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h3 className="text-stone-800 font-semibold text-sm">Available Actions</h3>
            <p className="text-stone-500 text-xs mt-0.5">
              Showing {visibleButtons.length} buttons from tenant's packages. Select which are available on <span className="font-medium text-stone-600">{terminal.registerName}</span>.
            </p>
          </div>
          {isDirty && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-4 min-h-[40px] text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>

        {/* Enable All toggle */}
        <div className="flex items-center justify-between p-3 rounded-xl border border-stone-200 bg-stone-50 mb-4">
          <div>
            <p className="text-stone-800 text-sm font-medium">Enable All</p>
            <p className="text-stone-500 text-xs">Toggle all {visibleButtons.length} buttons</p>
          </div>
          <button
            type="button"
            onClick={handleEnableAll}
            className={`relative inline-flex w-11 h-6 rounded-full shrink-0 transition-colors ${
              allEnabled ? 'bg-emerald-500' : 'bg-stone-300'
            }`}
          >
            <span className={`inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${
              allEnabled ? 'translate-x-[22px]' : 'translate-x-[2px]'
            }`} />
          </button>
        </div>

        {/* Button groups */}
        {groupedButtons.map((group) => (
          <div key={group.group} className="mb-4 last:mb-0">
            <p className="text-[10px] text-stone-400 uppercase tracking-wider font-semibold mb-2">{group.label}</p>
            <div className="space-y-1.5">
              {group.buttons.map((btn) => {
                const Icon = ICON_MAP[btn.icon] || Info;
                const isOn = available.includes(btn.id);
                return (
                  <div
                    key={btn.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                      isOn ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-100'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isOn ? 'bg-emerald-100' : 'bg-stone-100'
                    }`}>
                      <Icon size={16} className={isOn ? 'text-emerald-600' : 'text-stone-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isOn ? 'text-emerald-800' : 'text-stone-700'}`}>{btn.label}</p>
                      <p className="text-xs text-stone-500 mt-0.5 truncate">{btn.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle(btn.id)}
                      className={`relative inline-flex w-11 h-6 rounded-full shrink-0 transition-colors ${
                        isOn ? 'bg-emerald-500' : 'bg-stone-300'
                      }`}
                    >
                      <span className={`inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${
                        isOn ? 'translate-x-[22px]' : 'translate-x-[2px]'
                      }`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Enabling decides what the till MAY show; this is what makes it ready. */}
      <SetupPosCard
        tenantId={tenantId}
        registerId={registerId}
        registerName={terminal.registerName}
        enabledCount={initialAvailable.length}
        dirty={isDirty}
        lastSetup={lastSetup}
        onDone={() => { fetchRegister(); onSaved?.(); }}
      />
    </div>
  );
};

export default PosButtonLayoutTab;
